import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";

// Public pages
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Public info pages
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Coverage from "@/pages/Coverage";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminCustomers from "@/pages/admin/Customers";
import AdminFinance from "@/pages/admin/Finance";
import AdminProviders from "@/pages/admin/Providers";
import AdminSenderIDs from "@/pages/admin/SenderIDsAdmin";
import AdminSettings from "@/pages/admin/Settings";
import AdminMarketplace from "@/pages/admin/Marketplace";

// Customer pages
import CustomerLayout from "@/pages/customer/CustomerLayout";
import CustomerDashboard from "@/pages/customer/Dashboard";
import ComposeSMS from "@/pages/customer/ComposeSMS";
import Campaigns from "@/pages/customer/Campaigns";
import SentMessages from "@/pages/customer/SentMessages";
import Contacts from "@/pages/customer/Contacts";
import Wallet from "@/pages/customer/Wallet";
import BuyCredit from "@/pages/customer/BuyCredit";
import Subscribe from "@/pages/customer/Subscribe";
import TopUp from "@/pages/customer/TopUp";
import SenderIDs from "@/pages/customer/SenderIDs";
import APIKeys from "@/pages/customer/APIKeys";
import Profile from "@/pages/customer/Profile";
import Analytics from "@/pages/customer/Analytics";
import Marketplace from "@/pages/customer/Marketplace";
import Birthday from "@/pages/customer/Birthday";
import Team from "@/pages/customer/Team";
import PaymentCallback from "@/pages/PaymentCallback";
import AdminBlog from "@/pages/admin/AdminBlog";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#FAFAFC] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 text-sm font-medium">Loading Sendora...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) {
    if (user.role === "master_admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function AuthRedirect({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (user) {
    if (user.role === "master_admin") return <Navigate to="/admin" replace />;
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              style: { background: "#FFFFFF", color: "#0F172A", border: "1px solid #E2E8F0" },
            }}
          />
        <Routes>
          {/* Public */}
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<AuthRedirect><Login /></AuthRedirect>} />
          <Route path="/register" element={<AuthRedirect><Register /></AuthRedirect>} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/coverage" element={<Coverage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Admin */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute roles={["master_admin"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="finance" element={<AdminFinance />} />
            <Route path="providers" element={<AdminProviders />} />
            <Route path="sender-ids" element={<AdminSenderIDs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="marketplace" element={<AdminMarketplace />} />
            <Route path="blog" element={<AdminBlog />} />
          </Route>

          {/* Customer */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute roles={["customer_admin", "customer_staff"]}>
                <CustomerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<CustomerDashboard />} />
            <Route path="compose" element={<ComposeSMS />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="sent" element={<SentMessages />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="wallet" element={<Wallet />} />
            <Route path="wallet/buy" element={<BuyCredit />} />
            <Route path="subscribe" element={<Subscribe />} />
            <Route path="topup" element={<TopUp />} />
            <Route path="sender-ids" element={<SenderIDs />} />
            <Route path="api-keys" element={<APIKeys />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="marketplace" element={<Marketplace />} />
            <Route path="birthday" element={<Birthday />} />
            <Route path="team" element={<Team />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Payment callback */}
          <Route path="/payment/callback" element={<PaymentCallback />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;
